/* ============================================================
   Sweet Addison's cart engine (module).
   Adds a built box (or a single line) to the STOCK Shopify cart via
   /cart/add.js, then dispatches Horizon's native CartLinesUpdateEvent so
   the stock cart drawer auto-opens and every cart UI (bubble, drawer,
   cart page) re-renders. The money path (drawer + checkout) stays stock.

   NATIVE SUBSCRIPTIONS: when a selling_plan id is passed (from the tier ->
   plan map wired in admin at launch, [PENDING ADDISON APPROVAL]), it rides
   on the line as `selling_plan`, creating a real subscription contract at
   checkout. Until plans are wired, lines add as one-time and the ladder %
   is displayed honestly as pending (no fake discount is applied).

   BOX GROUPING: two modes.
   - Individual lines (default, works today, no admin setup): each flavor is
     its own cart line, all sharing box attributes for packer reconciliation.
   - Bundle parent (opt-in via SA_CONFIG.useBundleParent + bundleParentVariant):
     one grouped "Build a Box" parent line with the flavors as nested children
     (Shopify native nested cart lines via parent_id), ported from ws_bundle.
   ============================================================ */
import { CartLinesUpdateEvent } from '@shopify/events';

const SA = (window.SA = window.SA || {});
SA.config = window.SA_CONFIG || {};

function cartSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll('cart-items-component[data-section-id], cart-icon[data-section-id], [data-section-id].sa-header-section')
    .forEach((el) => el.dataset.sectionId && ids.add(el.dataset.sectionId));
  // Header cart bubble lives in the header section; include it if identifiable.
  document.querySelectorAll('#shopify-section-cart-drawer-section').forEach(() => ids.add('cart-drawer-section'));
  return [...ids];
}

async function postAddAndAnnounce(items, linesForEvent) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'standard-action',
      lines: linesForEvent,
      promise: deferred.promise,
    })
  );
  try {
    const secs = cartSectionIds();
    const body = { items };
    if (secs.length) body.sections = secs.join(',');
    const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let msg = 'We could not add that to your cart.';
      try {
        const j = await res.json();
        msg = j.description || j.message || msg;
      } catch (e) {}
      throw new Error(msg);
    }
    const added = await res.json();
    const ajaxCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'sa-build-a-box',
        sourceId: 'sa',
        itemCount: items.reduce((a, i) => a + (i.quantity || 1), 0),
        sections: added.sections,
        didError: false,
      },
    });
    return ajaxCart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

/**
 * Add a built box.
 * @param {Array<{id:number|string, quantity:number, priceCents?:number}>} flavors
 * @param {'sub'|'one'} mode
 * @param {Object} opts { boxSize, tierPct, sellingPlanId }
 */
SA.addBox = function (flavors, mode, opts = {}) {
  const clean = (flavors || []).filter((f) => f && f.id && f.quantity > 0);
  if (!clean.length) return Promise.reject(new Error('Add at least one flavor.'));
  const boxSize = opts.boxSize || clean.reduce((a, f) => a + f.quantity, 0);
  const sellingPlan = mode === 'sub' ? opts.sellingPlanId || SA.planForTier(boxSize) : null;
  const boxId = 'box-' + Date.now();
  const isWholesale = !!opts.isWholesale;
  // Line property printed by the cart drawer + checkout: say it is a subscription, on which
  // cadence (the name of the plan the line really carries), and the % off for this tier.
  // Wholesale accounts are one-time only (no selling_plan ever attaches), so the "Subscriber
  // ladder" property key itself reads as subscription language at checkout for them even
  // though the value is just the box size. Omitted for wholesale only; retail (sub + one-time)
  // keeps the property unchanged.
  const planName = String(((window.SA_CONFIG || SA.config || {}).planNames || {})[String(sellingPlan)] || 'Every 3 weeks');
  const ladder = sellingPlan
    ? `Subscription · ${planName.toLowerCase()}${opts.tierPct ? ` · ${opts.tierPct}% off` : ''}`
    : `${boxSize} cookies`;
  const boxProps = isWholesale
    ? { _box_id: boxId, 'Box Size': String(boxSize) }
    : { _box_id: boxId, 'Box Size': String(boxSize), 'Subscriber ladder': ladder };

  // ---- Bundle-parent path (opt-in, launch-config) ----
  if (SA.config.useBundleParent && SA.config.bundleParentVariant) {
    const parent = { id: Number(SA.config.bundleParentVariant), quantity: 1, properties: boxProps };
    if (sellingPlan) parent.selling_plan = Number(sellingPlan);
    const items = [parent];
    clean.forEach((f) => {
      const child = { id: Number(f.id), quantity: f.quantity, parent_id: Number(SA.config.bundleParentVariant),
        properties: { _box_id: boxId } };
      items.push(child);
    });
    const evLines = items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
    return postAddAndAnnounce(items, evLines);
  }

  // ---- Individual-lines path (default, works today) ----
  const items = clean.map((f) => {
    const it = { id: Number(f.id), quantity: f.quantity, properties: boxProps };
    if (sellingPlan) it.selling_plan = Number(sellingPlan);
    return it;
  });
  const evLines = items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
  return postAddAndAnnounce(items, evLines);
};

/** Single line add (PDP one-time or solo-flavor subscription). */
SA.addSingle = function (variantId, quantity, sellingPlanId, properties) {
  const it = { id: Number(variantId), quantity: quantity || 1 };
  if (sellingPlanId) it.selling_plan = Number(sellingPlanId);
  if (properties) it.properties = properties;
  return postAddAndAnnounce([it], [{ merchandiseId: String(variantId), quantity: quantity || 1 }]);
};

/** Map a box size to a native selling plan id from the tier->plan map (launch-wired). */
SA.planForTier = function (size) {
  const map = SA.config.tierPlanMap || {};
  // choose the highest tier threshold <= size that has a plan id
  const tiers = Object.keys(map).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b);
  let plan = SA.config.defaultSellingPlan || null;
  tiers.forEach((t) => { if (size >= t && map[t]) plan = map[t]; });
  return plan;
};
